import React, { useEffect, useState } from "react";

import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import WarningIcon from "@mui/icons-material/Warning";
import {
    CircularProgress,
    styled,
    Tooltip,
    tooltipClasses,
} from "@mui/material";
import Button from "@mui/material/Button";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";

import Card from "react-bootstrap/Card";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import {
    getDestinationIcon,
    getDestinationName,
    getSourceIcon,
    getSourceName,
} from "helpers/leads/icon";
import {
    deleteConnection,
    editConnection,
} from "helpers/leads/leadConnection/deleteConnection";

import EmptyState from "components/atoms/EmptyState";
import PageContainer from "components/atoms/PageContainer";
import { ConfirmationModal } from "components/organisms/ConfirmationModal";
import SwalAlertComp from "components/SwalAlert";

import { getAllLeadConnectionAsync } from "../../redux/slices/leadConnection/leadConnection.asyncActions";
import { LogoIcon } from "../shared/CardStyles";

const SourceErrorTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: "#000000",
        color: "#000",
        maxWidth: 220,
        fontSize: "12px",
        borderRadius: "10px",
        fontFamily: '"Nunito", serif',
        padding: "15px",
        boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
    },
    [`& .${tooltipClasses.tooltip} p`]: {
        fontWeight: "bold",
        fontFamily: '"Nunito", serif',
        color: "white",
        fontSize: "14px",
    },
    [`& .${tooltipClasses.tooltip} span`]: {
        fontWeight: "300",
        fontFamily: '"Nunito", serif',
        color: "white",
        fontSize: "12px",
    },
}));
const Connection = () => {
    const history = useNavigate();
    const language = useSelector((state) => state.languagesState.languages);
    const connection = useSelector(
        (state) =>
            state.leadAllConnectionState.getAllLeadConnection.leadConnection,
    );
    const loading = useSelector(
        (state) => state.leadAllConnectionState.getAllLeadConnection.status,
    );
    const dispatch = useDispatch();
    const [isUpdating, setIsUpdating] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingId, setPendingId] = useState(null);

    useEffect(() => {
        dispatch(getAllLeadConnectionAsync());
    }, [dispatch]);

    const handleCreateButton = () => {
        history(`/leads-wizard`);
    };

    const handleChangeActivePassive = (event, id) => {
        if (isUpdating) return;
        changedStatusOfConnection(event.target.checked, id);
    };

    const changedStatusOfConnection = async (checked, id) => {
        setIsUpdating(true);
        try {
            let data = {
                status: checked ? 1 : 0,
            };
            let response = await editConnection(data, id);
            if (response.status) {
                dispatch(getAllLeadConnectionAsync());
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message,
                    icon: "error",
                });
            }
        } finally {
            setIsUpdating(false);
        }
    };

    const GoToConnection = (id) => {
        history(`/connection/${id}`);
    };

    const deleteHandler = async (id) => {
        let response = await deleteConnection(id);
        if (response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: response.message,
                icon: "success",
                timer: 3000,
            });
            dispatch(getAllLeadConnectionAsync());
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message,
                icon: "error",
            });
        }
    };

    return (
        <PageContainer
            title={language?.sidebar?.connections ?? "Connections"}
            subtitle={
                language?.connections?.connections_desc ??
                "On this page, you can view connections, and create a new one by using the button."
            }
            additionalButtons={
                <Button
                    data-cy="lead-connection-create"
                    onClick={handleCreateButton}
                    color="primary"
                    variant="contained"
                >
                    {language?.connections?.create_connection ??
                        "Create Connection"}
                </Button>
            }
            loading={
                loading === "idle" ||
                loading === "loading" ||
                loading === "failed"
            }
            variant="list"
            containerClassName="tracker-detail-area"
        >
            <ConfirmationModal
                open={confirmOpen}
                onClose={() => {
                    setConfirmOpen(false);
                    setPendingId(null);
                }}
                onConfirm={() => {
                    deleteHandler(pendingId);
                    setConfirmOpen(false);
                    setPendingId(null);
                }}
                title={
                    language?.delete_swal_modal?.are_you_sure ?? "Are you sure?"
                }
                message="This action cannot be undone."
                confirmColor="error"
            />

            {!connection?.length ? (
                <EmptyState
                    icon={<HubOutlinedIcon />}
                    title={
                        language?.connections?.no_connections_yet ??
                        "No connections yet"
                    }
                    description={
                        language?.connections?.no_connections_yet_desc ??
                        "Create your first connection to link a source and destination for your leads."
                    }
                />
            ) : (
            <>
            <Grid
                container
                columnSpacing={{ xs: 4, sm: 12, md: 12 }}
                className="trackers-area connections-header-area mb-3"
            >
                <Grid
                    size={{
                        xs: 4,
                        sm: 12,
                        md: 12,
                        lg: 4,
                    }}
                >
                    <span class="title">
                        {language?.connections?.sources ?? "Sources(s)"}
                    </span>
                </Grid>
                <Grid
                    size={{
                        xs: 4,
                        sm: 12,
                        md: 12,
                        lg: 4,
                    }}
                    style={{ paddingLeft: 0 }}
                >
                    <span class="title text-center">
                        {language?.connections?.connections ?? "Connection(s)"}
                    </span>
                </Grid>
                <Grid
                    size={{
                        xs: 4,
                        sm: 12,
                        md: 12,
                        lg: 4,
                    }}
                    style={{ paddingLeft: 0 }}
                >
                    <span class="title text-right">
                        {language?.connections?.destinations ??
                            "Destinations(s)"}
                    </span>
                </Grid>
            </Grid>

            {connection?.map((item, index) => (
                <React.Fragment key={item.id}>
                    {index > 0 && <hr className="my-5"></hr>}
                    <Grid
                        data-cy="lead-connection-row"
                        container
                        rowSpacing={1}
                        columnSpacing={{ xs: 4, lg: 4, sm: 12, md: 12 }}
                        className="trackers-area connections-grid-area mb-0"
                    >
                        <Grid
                            size={{
                                xs: 4,
                                sm: 12,
                                md: 12,
                                lg: 4,
                            }}
                        >
                            <Card
                                data-cy="lead-connection-source"
                                className={`dashboard-tree-source-item no-effect dashboard-tree-source-item-${index} lead-item cursor-pointer`}
                                onClick={() => {
                                    GoToConnection(item?.id);
                                }}
                                style={{
                                    boxShadow: "none",
                                    border: "1px solid",
                                    borderRadius: "10px",
                                    borderColor: !item?.source_status
                                        ? "red"
                                        : "#E7E7E7",
                                    zIndex: 0,
                                }}
                            >
                                <CardContent
                                    style={{ padding: "20px" }}
                                    className="d-flex items-center justify-start"
                                >
                                    <Typography className="mr-3">
                                        <div className="item-icon-area">
                                            <LogoIcon
                                                src={getSourceIcon(
                                                    item?.source_type_code,
                                                )}
                                            />
                                        </div>
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 18,
                                            fontWeight: "700",
                                            fontFamily: "Nunito, sans-serif",
                                            color: !item?.source_status
                                                ? "red"
                                                : "black",
                                        }}
                                    >
                                        {getSourceName(item?.source_type_code)}
                                    </Typography>
                                    {!item?.source_status && (
                                        <SourceErrorTooltip
                                            placement="top"
                                            style={{ marginLeft: "10px" }}
                                            title={
                                                <React.Fragment>
                                                    <Typography color="white">
                                                        {"Error"}
                                                    </Typography>
                                                    <span>
                                                        {
                                                            "Since the Access Token is expired, you must reconnect the source."
                                                        }
                                                    </span>
                                                </React.Fragment>
                                            }
                                        >
                                            <WarningIcon
                                                style={{
                                                    verticalAlign: "sub",
                                                    marginLeft: "10px",
                                                    color: "red",
                                                }}
                                            />
                                        </SourceErrorTooltip>
                                    )}
                                </CardContent>
                                <CardContent
                                    style={{ padding: "25px 20px" }}
                                    className="d-flex items-center justify-start"
                                >
                                    <div className="lead-service-info-area">
                                        <span
                                            style={{
                                                display: "block",
                                                fontSize: "14px",
                                                color: "black",
                                                fontWeight: "500",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    display: "block",
                                                    fontSize: "12px",
                                                    color: "#ABABAB",
                                                    fontWeight: "400",
                                                }}
                                            >
                                                {language?.connections?.name ??
                                                    "Name"}
                                            </span>
                                            {item?.source}
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid
                            className="p-0"
                            size={{
                                xs: 4,
                                sm: 12,
                                md: 12,
                                lg: 4,
                            }}
                        >
                            <div className="lead-connection-detail-area">
                                <div className="lead-connection-detail">
                                    <div className="lead-connection-detail-switch">
                                        <Switch
                                            data-cy="lead-connection-status"
                                            color="secondary"
                                            checked={
                                                item.status && item.status
                                                    ? true
                                                    : false
                                            }
                                            disabled={isUpdating}
                                            onChange={(event) =>
                                                handleChangeActivePassive(
                                                    event,
                                                    item.id,
                                                )
                                            }
                                            inputProps={{
                                                "aria-label": "controlled",
                                            }}
                                        />
                                    </div>
                                    <Link
                                        data-cy="lead-connection-name"
                                        className="lead-connection-detail-name cursor-pointer"
                                        style={{ textDecoration: "none" }}
                                        to={`/connection/${item?.id}`}
                                    >
                                        <span>{item?.name}</span>
                                    </Link>
                                    <div className="lead-connection-detail-remove">
                                        <a
                                            data-cy="lead-connection-delete"
                                            href="javascript:;"
                                            onClick={() => {
                                                setPendingId(item.id);
                                                setConfirmOpen(true);
                                            }}
                                        >
                                            <DeleteOutlineOutlinedIcon></DeleteOutlineOutlinedIcon>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </Grid>
                        <Grid
                            size={{
                                xs: 4,
                                sm: 12,
                                md: 12,
                                lg: 4,
                            }}
                        >
                            <Card
                                data-cy="lead-connection-destination"
                                className={`dashboard-tree-source-item no-effect dashboard-tree-source-item-${index} lead-item cursor-pointer`}
                                onClick={() => {
                                    GoToConnection(item?.id);
                                }}
                                style={{
                                    boxShadow: "none",
                                    border: "1px solid",
                                    borderRadius: "10px",
                                    borderColor: !item?.destination_status
                                        ? "red"
                                        : "#E7E7E7",
                                }}
                            >
                                <CardContent
                                    style={{ padding: "20px" }}
                                    className="d-flex items-center justify-start"
                                >
                                    <Typography className="mr-3">
                                        <div className="item-icon-area">
                                            <LogoIcon
                                                src={getDestinationIcon(
                                                    item?.destination_type_code,
                                                )}
                                            />
                                        </div>
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 18,
                                            fontWeight: "700",
                                            fontFamily: "Nunito, sans-serif",
                                            color: "black",
                                        }}
                                    >
                                        {getDestinationName(
                                            item?.destination_type_code,
                                        )}
                                        {!item?.destination_status && (
                                            <SourceErrorTooltip
                                                placement="top"
                                                style={{
                                                    marginLeft: "10px",
                                                }}
                                                title={
                                                    <React.Fragment>
                                                        <Typography color="white">
                                                            {"Error"}
                                                        </Typography>
                                                        <span>
                                                            {
                                                                "Since the Access Token is expired, you must reconnect the source."
                                                            }
                                                        </span>
                                                    </React.Fragment>
                                                }
                                            >
                                                <WarningIcon
                                                    style={{
                                                        verticalAlign: "sub",
                                                        marginLeft: "10px",
                                                        color: "red",
                                                    }}
                                                />
                                            </SourceErrorTooltip>
                                        )}
                                    </Typography>
                                </CardContent>
                                <CardContent
                                    style={{ padding: "25px 20px" }}
                                    className="d-flex items-center justify-start"
                                >
                                    <div className="lead-service-info-area">
                                        <span
                                            style={{
                                                display: "block",
                                                fontSize: "14px",
                                                color: "black",
                                                fontWeight: "500",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    display: "block",
                                                    fontSize: "12px",
                                                    color: "#ABABAB",
                                                    fontWeight: "400",
                                                }}
                                            >
                                                {language?.connections?.name ??
                                                    "Name"}
                                            </span>
                                            {item?.destination}
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </React.Fragment>
            ))}
            </>
            )}
        </PageContainer>
    );
};

export default Connection;
